import { createFileRoute, Link, useParams, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  MapPin,
  QrCode,
  Calendar,
  CheckCircle2,
  Navigation,
  Sparkles,
} from "lucide-react";
import { useEffect, useState } from "react";
import { getTempleById, hasVisit, insertVisit } from "@/lib/yatra-db";
import { useAuth } from "@/lib/auth";
import { useI18n, pickName, pickHistory } from "@/lib/i18n";
import { toast } from "sonner";
import bannerImg from "@/assets/temple-banner.jpg";
import mandala from "@/assets/mandala-hero.png";

type Temple = {
  id: string;
  name_en: string;
  name_hi: string;
  name_gu: string;
  area: string;
  yatra_order: number;
  mool_nayak: string;
  established_year: number | null;
  latitude: number | null;
  longitude: number | null;
  history_en: string;
  history_hi: string;
  history_gu: string;
  qr_code: string;
  image_url: string | null;
};

export const Route = createFileRoute("/app/temples/$id")({ component: TempleDetail });

function TempleDetail() {
  const { id } = useParams({ from: "/app/temples/$id" });
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [temple, setTemple] = useState<Temple | null>(null);
  const [visited, setVisited] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const data = getTempleById(id);
      setTemple(data as Temple | null);
      if (user) {
        setVisited(hasVisit(user.id, id));
      }
    })();
  }, [id, user]);

  const checkIn = async () => {
    if (!user || !temple) {
      return;
    }
    setLoading(true);
    const res = insertVisit(user.id, temple.id);
    setLoading(false);
    if (!res.ok) {
      return toast.error(res.message);
    }
    setVisited(true);
    toast.success("🙏 " + t("welcome"));
  };

  if (!temple) {
    return <div className="px-5 pt-16 text-sm text-muted-foreground">{t("loading")}</div>;
  }

  const mapsUrl =
    temple.latitude && temple.longitude
      ? `https://www.google.com/maps/search/?api=1&query=${temple.latitude},${temple.longitude}`
      : `https://www.google.com/maps/search/${encodeURIComponent(pickName(temple, "en") + " " + temple.area + " Ahmedabad")}`;

  return (
    <div className="pb-6">
      {/* hero banner */}
      <div className="relative h-56 overflow-hidden">
        <img
          src={temple.image_url || bannerImg}
          alt={pickName(temple, lang)}
          className="h-full w-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, color-mix(in oklab, hsl(var(--maroon)) 35%, transparent) 0%, color-mix(in oklab, hsl(var(--maroon)) 12%, transparent) 45%, hsl(var(--background)) 100%)",
          }}
        />
        <img
          src={mandala}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 opacity-30 mix-blend-overlay animate-spin-slow"
        />
        <Link
          to="/app/temples"
          className="absolute left-4 top-14 inline-flex items-center gap-1 rounded-full glass px-3 py-1.5 text-xs font-semibold shadow-sm"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("back")}
        </Link>
        <div className="absolute right-4 top-14 flex gap-1.5">
          <span className="rounded-full glass px-2.5 py-1 text-[10px] font-bold">
            #{temple.yatra_order}
          </span>
          {visited && (
            <span className="flex items-center gap-1 rounded-full bg-primary px-2 py-1 text-[10px] font-bold text-primary-foreground shadow-md">
              <CheckCircle2 className="h-3 w-3" /> {t("visited")}
            </span>
          )}
        </div>
      </div>

      {/* title card overlapping banner */}
      <div className="relative mx-5 -mt-12 rounded-2xl border border-border bg-card p-4 shadow-[var(--shadow-soft)] animate-scale-pop">
        <div className="flex items-start gap-2">
          <Sparkles className="mt-1 h-4 w-4 text-primary animate-flicker" />
          <div className="flex-1">
            <h1 className="font-serif text-xl font-bold leading-tight">{pickName(temple, lang)}</h1>
            <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              {temple.area}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 px-5 stagger">
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-primary">
            {t("moolNayak")}
          </div>
          <div className="mt-0.5 text-sm font-semibold">{temple.mool_nayak}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-primary">
            {t("established")}
          </div>
          <div className="mt-0.5 text-sm font-semibold">{temple.established_year ?? "—"}</div>
        </div>
      </div>

      <div className="mt-4 px-5">
        <h2 className="font-serif text-base font-bold flex items-center gap-2">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent to-saffron" />
          {t("history_title")}
          <span className="h-px flex-1 bg-gradient-to-l from-transparent to-saffron" />
        </h2>
        <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground/85">
          {pickHistory(temple, lang) || "History coming soon."}
        </p>
      </div>

      <div className="mt-5 space-y-2 px-5">
        {!visited ? (
          <button onClick={checkIn} disabled={loading} className="btn-primary w-full">
            <CheckCircle2 className="h-4 w-4" /> {loading ? t("loading") : t("checkIn")}
          </button>
        ) : (
          <div className="flex items-center justify-center gap-2 rounded-xl bg-green-100 p-3 text-center text-xs font-bold text-green-700">
            <CheckCircle2 className="h-4 w-4" />
            {t("alreadyVisited")}
          </div>
        )}
        <div className="grid grid-cols-3 gap-2">
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card py-3 text-[10px] font-semibold transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <Navigation className="h-4 w-4 text-primary" />
            {t("navigate")}
          </a>
          <button
            onClick={() => nav({ to: "/app/scan" })}
            className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card py-3 text-[10px] font-semibold transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <QrCode className="h-4 w-4 text-primary" />
            {t("scanQr")}
          </button>
          <button
            onClick={() => nav({ to: "/app/bookings" })}
            className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card py-3 text-[10px] font-semibold transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <Calendar className="h-4 w-4 text-primary" />
            Book
          </button>
        </div>
      </div>
    </div>
  );
}
